import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { EditMtrForm } from 'components/EditMtrForm';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { PriceListMtrRowData, WorkOrderChangePriceItem } from 'gql/graphql';
import { MtrRecordStatusEnum, ChapterEnum, MtrTypeEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { dataConvert, SupplyByItems } from 'pages/Project/UerProject/PriceList/EditMtrDialog';
import { priceListMtrRow } from 'pages/Project/UerProject/PriceList/EditMtrDialog/editMtrQueries';
import type { AnyObject } from 'types/AnyObject';
import { replacerCost } from 'utils/replacerCost';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useIsUniquePriceListMtrRowKey } from '../../WorkOrder/CreateMtrDialog';
import { useIsUniquePriceListMtrCode } from '../../WorkOrder/CreateMtrDialog/useIsUniquePriceListMtrCode';
import { updateWorkOrderChangeMtr } from '../WorkOrderChangeQueries';

const keyKey = 'key';
const statusKey = 'status';
const codeValueKey = 'code';
const mtrTypeValueKey = 'mtrType';
const supplyByValueKey = 'supplyBy';

export const EditWorkOrderChangeMtrDialog = ({
    isModalOpened,
    closeModalHandler,
    mtrId,
    workOrderChangePriceId,
    refreshTableDataHandler,
    chapter,
    rowData,
}: {
    isModalOpened: boolean;
    closeModalHandler: () => void;
    mtrId: number | undefined;
    workOrderChangePriceId: number | undefined;
    refreshTableDataHandler?: (objectIds: number[]) => void;
    chapter: ChapterEnum | undefined | null;
    rowData?: WorkOrderChangePriceItem;
}) => {
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId);
    const contractId = useMemo(() => projectData?.contractId, [projectData?.contractId]);

    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement>(null);
    const { control, handleSubmit, reset, formState, watch, setError, clearErrors } = useForm<any>({
        defaultValues: {},
    });
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);

    const keyValue = watch?.(keyKey);
    const codeValue = watch?.(codeValueKey);
    const mtrTypeValue = watch?.(mtrTypeValueKey);
    const supplyByValue = watch?.(supplyByValueKey);
    const statusValue = watch?.(statusKey);

    const isDirtyKeyValue = formState?.dirtyFields?.[keyKey];
    const isDirtyCodeValue = formState?.dirtyFields?.[codeValueKey];

    const { result: isKeyValueUnique } = useIsUniquePriceListMtrRowKey({
        contractId,
        mtrRowId: mtrId ? Number(mtrId) : null,
        isDirtyCodeValue: isDirtyKeyValue,
        key: keyValue,
    });

    const { result: isCodeValueUnique } = useIsUniquePriceListMtrCode({
        contractId,
        mtrRowId: mtrId ? Number(mtrId) : null,
        code: codeValue,
        isDirtyCodeValue,
        mtrType: mtrTypeValue?.id,
        supplyBy: supplyByValue?.id,
    });

    useEffect(() => {
        if (keyValue && !isKeyValueUnique) {
            setError?.(keyKey, { message: 'МТР с таким кодом уже существует' });
        } else {
            clearErrors?.(keyKey);
        }
    }, [clearErrors, isKeyValueUnique, keyValue, setError]);

    useEffect(() => {
        if (codeValue && !isCodeValueUnique) {
            setError?.(codeValueKey, {
                message: 'Шифр МТР уже существует в рамках выбранного типа и оператора поставки',
            });
        } else {
            clearErrors?.(codeValueKey);
        }
    }, [clearErrors, codeValue, isCodeValueUnique, isKeyValueUnique, keyValue, setError]);

    const mtrRowDataVariables = useMemo(
        () => ({
            mtrRowId: mtrId,
        }),
        [mtrId],
    );

    const [mtrData] = useRequest({
        requestId: 'workOrderChangeMtrData',
        query: priceListMtrRow,
        variables: mtrRowDataVariables,
        withEffect: Boolean(mtrId),
    });

    const [resetMtrRowData] = useResetStoreData({ requestId: 'workOrderChangeMtrData' });
    const [resetUpdateResultData] = useResetStoreData({ requestId: 'updateWorkOrderChangeMtr' });

    const resetAllEditModalData = useCallback(() => {
        resetMtrRowData(undefined);
        resetUpdateResultData(undefined);
    }, [resetMtrRowData, resetUpdateResultData]);

    const preparedData = useMemo(
        () => mtrData?.uerPriceListMtrRow?.data && dataConvert(mtrData.uerPriceListMtrRow.data as PriceListMtrRowData),
        [mtrData?.uerPriceListMtrRow?.data],
    );

    useEffect(() => {
        if (preparedData) {
            reset(preparedData);
        }

        return () => {
            resetAllEditModalData();
        };
    }, [preparedData, reset, resetAllEditModalData]);

    const objectIdsArrEditMutation = useMemo(() => [rowData?.workOrderObjectId], [rowData?.workOrderObjectId]);

    const successUpdateHandler = useCallback(() => {
        refreshTableDataHandler?.(objectIdsArrEditMutation);
        closeModalHandler?.();
        resetAllEditModalData();
    }, [closeModalHandler, objectIdsArrEditMutation, refreshTableDataHandler, resetAllEditModalData]);

    const [, updateResultStatus, , updateRequest] = useRequest({
        requestId: 'updateWorkOrderChangeMtr',
        query: updateWorkOrderChangeMtr,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });

    const submitHandler = useCallback(
        (submitData: AnyObject) => {
            const variables = {
                workOrderChangePriceId,
                input: {
                    mtrRowId: Number(mtrId),
                    ...(submitData.key && { key: submitData.key }),
                    ...(submitData.code && { code: submitData.code }),
                    ...(submitData.unitWeight && { unitWeight: replacerCost(submitData.unitWeight) }),
                    ...(submitData.unitCost && { unitCost: replacerCost(submitData.unitCost) }),
                    ...(submitData.supplyBy.id && { supplyBy: submitData.supplyBy.id }),
                    ...(submitData.mtrType.id && { mtrType: submitData.mtrType.id }),
                    ...(submitData.justification && { justification: submitData.justification }),
                },
            };

            dispatch(updateRequest({ ...variables }));
        },
        [dispatch, mtrId, updateRequest, workOrderChangePriceId],
    );

    const mtrTypeItems = useMemo(
        () => [
            ...(![ChapterEnum.Chapter8, ChapterEnum.Chapter9].includes(chapter as ChapterEnum)
                ? [
                      {
                          id: MtrTypeEnum.Equipment,
                          label: 'Оборудование',
                      },
                  ]
                : []),
            {
                id: MtrTypeEnum.Material,
                label: 'Материал',
            },
        ],
        [chapter],
    );

    const handleCloseEditMtr = useCallback(() => {
        if (formState.isDirty) {
            setIsOpenWarningDialog(true);
        } else {
            closeModalHandler();
        }
    }, [closeModalHandler, formState.isDirty]);

    const handleSubmitWarningDialog = useCallback(() => {
        closeModalHandler();
    }, [closeModalHandler]);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const isCodeFieldDisabled = useMemo(() => {
        const editableStatuses = [MtrRecordStatusEnum.NewRecord, MtrRecordStatusEnum.Draft];
        const isEditable = editableStatuses.includes(statusValue);

        return !isEditable;
    }, [statusValue]);

    return (
        <ModalDialog
            title="Редактирование МТР"
            onConfirm={handleSubmit(submitHandler)}
            onClose={handleCloseEditMtr}
            isOpen={isModalOpened}
            withTitleCloseButton
            btnConfirmLabel="Сохранить"
            btnConfirmDisabled={!formState.isDirty || !isKeyValueUnique || !isCodeValueUnique}
            isCommit={updateResultStatus === RequestStatus.FETCHING}
            isOnClickOutsideDisable
        >
            <EditMtrForm
                ref={formRef}
                control={control}
                supplyByItems={SupplyByItems}
                mtrTypeItems={mtrTypeItems}
                hideInformer
                disabledFieldRules={{
                    code: isCodeFieldDisabled,
                }}
            />

            <ConfirmationDialog
                textHeader="ОТМЕНА РЕДАКТИРОВАНИЯ МТР"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                onSubmit={handleSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={handleCancelWarningDialog}
            />
        </ModalDialog>
    );
};

EditWorkOrderChangeMtrDialog.displayName = 'EditWorkOrderChangeMtrDialog';
